import { useEffect, type ReactElement } from 'react';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { startTrellusHost } from '../../../lib/trellus/host-events-bridge';
import { useTrellusCompletionSync } from './useTrellusCompletionSync';

/**
 * Connects the document to the Trellus extension once (mounted in
 * `AuthenticatedLayout`) and disposes every listener/timer on unmount. Renders
 * nothing. An absent extension is normal: the handshake times out after 3 s
 * and every Trellus-aware surface stays inert.
 *
 * StrictMode: `startTrellusHost()` builds a brand-new client on every call and
 * keeps no module-level "started" flag, so setup → cleanup → setup leaves
 * exactly one live client; the effect carries no refs.
 *
 * `useTrellusCompletionSync` (gong reconcile U20) tells the server once about
 * each confirmed completion / removal so reconcile re-checks that row.
 *
 * A demo session never connects: the extension would dial real prospects, and
 * with no host the headset button stays absent.
 */
export function TrellusHostConnection(): ReactElement | null {
  const { impersonation } = useUserControlsContext();
  return impersonation?.target === 'demo' ? null : <TrellusHostRuntime />;
}

function TrellusHostRuntime(): null {
  useEffect(() => startTrellusHost(), []);
  useTrellusCompletionSync();
  return null;
}
